/**
 * MFA setup wizard for clinicians and admins.
 *
 * Three-step flow:
 *   1. Setup — POST /api/security/mfa/setup returns a `otpauth://` URI +
 *      the base32 secret. We render the URI as a QR code and expose the
 *      secret for manual entry (typing into an authenticator that can't
 *      scan).
 *   2. Verify — user enters the 6-digit code from their app. On success
 *      the server activates MFA and returns 10 single-use recovery codes.
 *   3. Recovery codes — displayed ONCE. User must acknowledge they've
 *      downloaded/printed them before the "I've saved my codes" button
 *      unlocks. Print + Download-.txt are provided.
 *
 * Access rule: this route is gated to authenticated users only. Once
 * `mfa_enabled` is true, the wizard redirects to /account/security.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Check, Copy, Download, Loader2, Printer, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import api from "@/lib/api";
import { useAuth, formatErr } from "@/lib/auth";

export default function MfaSetup() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("intro"); // intro | scan | verify | codes | done
  const [setup, setSetup] = useState(null); // { provisioning_uri, secret }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [codes, setCodes] = useState([]); // 10 recovery codes, displayed once
  const [acknowledged, setAcknowledged] = useState(false);
  const printableRef = useRef(null);

  // If the user has already enrolled and lands here by accident, bounce them.
  useEffect(() => {
    if (user && user.mfa_enabled) nav("/account/security", { replace: true });
  }, [user, nav]);

  const beginSetup = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/security/mfa/setup", {});
      setSetup(data);
      setStep("scan");
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const submitVerify = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/security/mfa/verify", { code: code.trim() });
      setCodes(data.recovery_codes || []);
      // Reflect the new MFA-enabled state locally so downstream routes
      // (e.g. the Protected wrapper) stop bouncing this user to /mfa/setup.
      if (user) setUser({ ...user, mfa_enabled: true, mfa_setup_required: false });
      setStep("codes");
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const copySecret = () => {
    if (!setup?.secret) return;
    navigator.clipboard?.writeText(setup.secret);
    toast.success("Secret copied");
  };

  const downloadCodes = () => {
    const stamp = new Date().toISOString().replace(/[:.]/g, "-");
    const contents = [
      "Functional LAB — MFA recovery codes",
      `Account: ${user?.email || ""}`,
      `Generated: ${new Date().toString()}`,
      "",
      "Each code works once. Keep them somewhere only you can reach — a password",
      "manager, a locked note, or a printed copy in a drawer.",
      "",
      ...codes,
    ].join("\n");
    const blob = new Blob([contents], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `functional-lab-recovery-codes-${stamp}.txt`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    setAcknowledged(true);
  };

  const printCodes = () => {
    const win = window.open("", "_blank", "width=520,height=680");
    if (!win) { toast.error("Please allow pop-ups so we can open the printable page."); return; }
    const rows = codes.map((c) => `<li>${c}</li>`).join("");
    win.document.write(`<!doctype html>
<html><head><title>Functional LAB — recovery codes</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif; padding: 32px; color: #111; }
  h1 { font-size: 20px; margin: 0 0 4px; letter-spacing: 1px; text-transform: uppercase; }
  .sub { color: #666; font-size: 12px; margin-bottom: 24px; }
  .brand { color: #f47a20; font-weight: 700; letter-spacing: 4px; font-size: 11px; text-transform: uppercase; margin-bottom: 10px; }
  ul { padding: 0; list-style: none; border: 1px dashed #999; border-radius: 6px; }
  li { padding: 12px 20px; font-family: "SF Mono", "Menlo", monospace; font-size: 15px; letter-spacing: 2px; border-bottom: 1px dashed #ddd; }
  li:last-child { border-bottom: 0; }
  .note { margin-top: 20px; font-size: 12px; color: #666; line-height: 1.6; }
</style></head><body>
<div class="brand">Functional LAB</div>
<h1>MFA recovery codes</h1>
<div class="sub">Account: ${user?.email || ""} · Generated ${new Date().toLocaleString()}</div>
<ul>${rows}</ul>
<div class="note">
  Each code works exactly once. If you lose access to your authenticator app,
  paste any unused code on the sign-in screen to get back in — then regenerate
  a fresh set of 10 straight away from Security Settings.
</div>
<script>window.print();</script>
</body></html>`);
    win.document.close();
    setAcknowledged(true);
  };

  const finishSetup = () => {
    if (!acknowledged) {
      toast.error("Please download or print your recovery codes before finishing.");
      return;
    }
    toast.success("MFA is now active on your account.");
    nav(user?.role === "admin" ? "/admin" : "/clinician", { replace: true });
  };

  const totpValid = useMemo(() => /^\d{6}$/.test(code), [code]);

  return (
    <div className="fl-page min-h-screen">
      <PublicHeader />
      <section className="pt-32 pb-24 px-6 bg-[#0A0A0A] fl-noise relative">
        <div className="max-w-2xl mx-auto relative">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-[#f47a20]/15 border border-[#f47a20]/40 flex items-center justify-center">
              <ShieldCheck size={22} className="text-[#f47a20]" />
            </div>
            <div>
              <div className="fl-label text-[#f47a20]">Two-factor authentication</div>
              <h1 className="fl-heading text-3xl md:text-4xl">Set up your authenticator</h1>
            </div>
          </div>

          <div className="fl-card p-6 md:p-8" data-testid="mfa-setup-card">
            {step === "intro" && (
              <div data-testid="mfa-step-intro">
                <p className="text-sm text-zinc-300 leading-relaxed">
                  Because you're a member of the Functional LAB team, we require an authenticator app on
                  every sign-in — this stops anyone with your password alone from reaching patient records.
                </p>
                <ol className="mt-5 space-y-3 text-sm text-zinc-400 list-decimal pl-5">
                  <li>Install Google Authenticator, 1Password, Authy or your preferred TOTP app.</li>
                  <li>Scan the QR code we're about to show you.</li>
                  <li>Enter the 6-digit code the app displays to confirm it works.</li>
                  <li>Download or print your recovery codes and keep them somewhere safe.</li>
                </ol>
                {err && <div className="mt-4 text-sm text-red-400" data-testid="mfa-error">{err}</div>}
                <button onClick={beginSetup} disabled={busy}
                        className="fl-btn mt-6 w-full justify-center"
                        data-testid="mfa-begin">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                  {busy ? "Preparing…" : "Get started"}
                </button>
              </div>
            )}

            {step === "scan" && setup && (
              <div data-testid="mfa-step-scan">
                <div className="fl-label text-[#f47a20]">Step 1 of 2</div>
                <h2 className="fl-heading text-2xl mt-2">Scan this QR code</h2>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed">
                  Open your authenticator app, tap "Add account" → "Scan a QR code" and point it here.
                </p>
                <div className="mt-6 flex justify-center">
                  <div className="p-4 bg-white rounded-md" data-testid="mfa-qr">
                    <QRCodeSVG value={setup.provisioning_uri} size={192} level="M" />
                  </div>
                </div>
                <div className="mt-6 text-xs">
                  <div className="fl-label text-zinc-400 mb-2">Can't scan? Type this key manually</div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 rounded-md border border-white/10 bg-white/[0.03] px-3 py-2 font-mono text-zinc-100 text-xs break-all"
                         data-testid="mfa-secret">{setup.secret}</div>
                    <button onClick={copySecret}
                            className="fl-btn-ghost text-xs inline-flex items-center gap-1 shrink-0"
                            data-testid="mfa-copy-secret">
                      <Copy size={12} /> Copy
                    </button>
                  </div>
                </div>
                <button onClick={() => setStep("verify")} className="fl-btn mt-8 w-full justify-center"
                        data-testid="mfa-goto-verify">
                  I've added the account — next
                </button>
              </div>
            )}

            {step === "verify" && (
              <form onSubmit={submitVerify} data-testid="mfa-step-verify">
                <div className="fl-label text-[#f47a20]">Step 2 of 2</div>
                <h2 className="fl-heading text-2xl mt-2">Enter the 6-digit code</h2>
                <p className="text-sm text-zinc-400 mt-2">
                  Your authenticator now shows a rotating code for "Functional LAB". Enter it below.
                </p>
                <input
                  className="fl-input mt-6 tracking-[0.6em] text-center text-lg"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  autoFocus
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  data-testid="mfa-code"
                />
                {err && <div className="mt-4 text-sm text-red-400" data-testid="mfa-verify-error">{err}</div>}
                <button className="fl-btn mt-6 w-full justify-center"
                        disabled={busy || !totpValid}
                        data-testid="mfa-verify-submit">
                  {busy ? "Verifying…" : "Verify & activate"}
                </button>
                <button type="button" onClick={() => setStep("scan")}
                        className="text-xs text-zinc-500 hover:text-[#f47a20] w-full mt-3"
                        data-testid="mfa-verify-back">
                  ← Back to the QR code
                </button>
              </form>
            )}

            {step === "codes" && (
              <div data-testid="mfa-step-codes">
                <div className="fl-label text-emerald-400">MFA active</div>
                <h2 className="fl-heading text-2xl mt-2">Save your recovery codes</h2>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed">
                  These 10 single-use codes are your only way back into the account if you lose your phone.
                  They will <span className="text-zinc-100 font-medium">never be shown again</span> — download or print them now.
                </p>
                <div ref={printableRef}
                     className="mt-6 rounded-md border border-white/10 bg-white/[0.02] p-4 grid grid-cols-2 gap-y-1.5 gap-x-4 font-mono text-sm"
                     data-testid="mfa-recovery-codes">
                  {codes.map((c) => (<div key={c} className="tracking-[0.15em] text-zinc-100">{c}</div>))}
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button onClick={downloadCodes} className="fl-btn-ghost text-sm inline-flex items-center gap-2"
                          data-testid="mfa-codes-download">
                    <Download size={14} /> Download .txt
                  </button>
                  <button onClick={printCodes} className="fl-btn-ghost text-sm inline-flex items-center gap-2"
                          data-testid="mfa-codes-print">
                    <Printer size={14} /> Print
                  </button>
                </div>
                <label className="mt-6 flex items-start gap-3 text-sm text-zinc-300 cursor-pointer">
                  <input type="checkbox" checked={acknowledged}
                         onChange={(e) => setAcknowledged(e.target.checked)}
                         data-testid="mfa-codes-ack" className="mt-1" />
                  <span>
                    I've stored my recovery codes somewhere safe. I understand they won't be shown again.
                  </span>
                </label>
                <button onClick={finishSetup}
                        disabled={!acknowledged}
                        className="fl-btn mt-6 w-full justify-center disabled:opacity-40"
                        data-testid="mfa-codes-finish">
                  <Check size={14} /> Continue
                </button>
              </div>
            )}
          </div>

          <div className="mt-6 text-xs text-zinc-500 text-center">
            <Link to="/account/security" className="hover:text-[#f47a20]" data-testid="mfa-settings-link">
              Manage MFA and recovery codes
            </Link>
          </div>
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}
